// PrecipRenderer — world-space rain/snow particles driven by WeatherState.
// Preallocated ring buffers, no per-frame allocations.

const RAIN_MAX = 600;
const SNOW_MAX = 380;

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function makeParticle() {
  return { x: 0, y: 0, vx: 0, vy: 0, fallOffset: 0, age: 0, life: 0, size: 0, drift: 0, alive: false };
}

export class PrecipRenderer {
  constructor() {
    this.rainBuf = [];
    this.snowBuf = [];
    for (let i = 0; i < RAIN_MAX; i++) this.rainBuf.push(makeParticle());
    for (let i = 0; i < SNOW_MAX; i++) this.snowBuf.push(makeParticle());
    this._rainIdx = 0;
    this._snowIdx = 0;
    this._rainAccum = 0;
    this._snowAccum = 0;
  }

  /**
   * @param {CanvasRenderingContext2D} ctx
   * @param {number} w - canvas width
   * @param {number} h - canvas height
   * @param {number} dtSec - delta time in seconds
   * @param {import('../../world/weather/WeatherState.mjs').WeatherState} ws
   */
  render(ctx, w, h, dtSec, ws, cloudSystem, camera) {
    if (!ws || !ws.enabled || ws.precip01 < 0.01) return;
    dtSec = Math.min(dtSec, 0.1);

    const isSnow = ws.mode === 'snow';
    const isRain = ws.mode === 'rain';
    if (!isSnow && !isRain) return;

    const clouds = this._gatherClouds(cloudSystem, camera, w, h);

    if (isRain) {
      this._updateRain(ctx, w, h, dtSec, ws, clouds, camera);
    } else {
      this._updateSnow(ctx, w, h, dtSec, ws, clouds, camera);
    }
  }

  _gatherClouds(cloudSystem, camera, w, h) {
    if (!cloudSystem) return [];
    const result = [];
    for (let li = 0; li < cloudSystem.layerCount; li++) {
      for (const item of cloudSystem.getGroundProjectedClouds(li, camera, w, h)) {
        const { cloud, scale } = item;
        const area = cloud.bounds.w * cloud.bounds.h * scale * scale;
        result.push({ ...item, weight: area * cloud.alpha });
      }
    }
    return result;
  }

  _sampleCloudPosition(clouds) {
    // 1. Pick cloud by weight
    let total = 0;
    for (const c of clouds) total += c.weight;
    let r = Math.random() * total;
    let picked = clouds[0];
    for (const c of clouds) { r -= c.weight; if (r <= 0) { picked = c; break; } }

    // 2. Pick circle within cloud by area (r²)
    const { cloud } = picked;
    const circles = cloud.circles;
    let cTotal = 0;
    for (const ci of circles) cTotal += ci.r * ci.r;
    r = Math.random() * cTotal;
    let sc = circles[0];
    for (const ci of circles) { r -= ci.r * ci.r; if (r <= 0) { sc = ci; break; } }

    // 3. Random point in world space
    const angle = Math.random() * Math.PI * 2;
    const dist = Math.sqrt(Math.random()) * sc.r * cloud.baseScale;
    return {
      wx: cloud.wx + sc.ox * cloud.baseScale + Math.cos(angle) * dist,
      wy: cloud.wy + sc.oy * cloud.baseScale + Math.sin(angle) * dist,
      height: picked.height,
    };
  }

  _updateRain(ctx, w, h, dtSec, ws, clouds, camera) {
    const precip = ws.precip01;
    const spawnRate = lerp(40, 220, precip);
    this._rainAccum += spawnRate * dtSec;

    // Spawn new particles
    while (this._rainAccum >= 1) {
      if (clouds.length === 0) { this._rainAccum = 0; break; }
      this._rainAccum -= 1;
      const p = this.rainBuf[this._rainIdx];
      this._rainIdx = (this._rainIdx + 1) % RAIN_MAX;
      const pos = this._sampleCloudPosition(clouds);
      p.x = pos.wx;
      p.y = pos.wy;
      p.fallOffset = -pos.height;
      p.vy = lerp(600, 1000, precip);
      p.vx = ws.wind.x * 12;
      p.age = 0;
      p.life = 10;
      p.size = lerp(8, 16, Math.random());
      p.alive = true;
    }

    // Update & render
    const alpha = lerp(0.08, 0.18, precip);
    ctx.strokeStyle = 'rgba(200,210,230,' + alpha + ')';
    ctx.lineWidth = 1;
    ctx.beginPath();

    for (let i = 0; i < RAIN_MAX; i++) {
      const p = this.rainBuf[i];
      if (!p.alive) continue;
      p.x += p.vx * dtSec;
      p.fallOffset += p.vy * dtSec;
      p.age += dtSec;

      // World → screen
      const screenX = (p.x - camera.x) * camera.zoom + w / 2;
      const screenY = (p.y - camera.y) * camera.zoom + h / 2 + p.fallOffset * camera.zoom;

      // Kill by screen bounds or age
      if (screenY > h + 30 || screenX < -50 || screenX > w + 50 || p.age > p.life) {
        p.alive = false;
        continue;
      }

      // Draw line segment in wind-angled direction
      const len = p.size;
      const dx = p.vx * 0.01;
      const dy = p.vy * 0.01;
      const mag = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = dx / mag * len;
      const ny = dy / mag * len;
      ctx.moveTo(screenX, screenY);
      ctx.lineTo(screenX + nx, screenY + ny);
    }
    ctx.stroke();
  }

  _updateSnow(ctx, w, h, dtSec, ws, clouds, camera) {
    const precip = ws.precip01;
    const spawnRate = lerp(25, 140, precip);
    this._snowAccum += spawnRate * dtSec;

    while (this._snowAccum >= 1) {
      if (clouds.length === 0) { this._snowAccum = 0; break; }
      this._snowAccum -= 1;
      const p = this.snowBuf[this._snowIdx];
      this._snowIdx = (this._snowIdx + 1) % SNOW_MAX;
      const pos = this._sampleCloudPosition(clouds);
      p.x = pos.wx;
      p.y = pos.wy;
      p.fallOffset = -pos.height;
      p.vy = lerp(30, 80, precip);
      p.vx = ws.wind.x * 3;
      p.age = 0;
      p.life = 30;
      p.size = 1 + Math.random() * 1.5;
      p.drift = Math.random() * Math.PI * 2;
      p.alive = true;
    }

    const alpha = lerp(0.06, 0.14, precip);
    ctx.fillStyle = 'rgba(255,255,255,' + alpha + ')';

    for (let i = 0; i < SNOW_MAX; i++) {
      const p = this.snowBuf[i];
      if (!p.alive) continue;
      p.x += p.vx * dtSec + Math.sin(p.age * 2 + p.drift) * 8 * dtSec;
      p.fallOffset += p.vy * dtSec;
      p.age += dtSec;

      // World → screen
      const screenX = (p.x - camera.x) * camera.zoom + w / 2;
      const screenY = (p.y - camera.y) * camera.zoom + h / 2 + p.fallOffset * camera.zoom;

      // Kill by screen bounds or age
      if (screenY > h + 30 || screenX < -30 || screenX > w + 30 || p.age > p.life) {
        p.alive = false;
        continue;
      }
      ctx.beginPath();
      ctx.arc(screenX, screenY, p.size, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
